import React from 'react'
import { Button, Col, Container, FormLabel, Row } from 'react-bootstrap'
import { Listing, ListingAction, SectionStyles } from '../../components/ui/Listing/Listing'

import { AppContext } from '../../App'
import * as Request from '../../utilities/request'
import { Invoice } from '../../../../back-end/common/invoice'

import NewInvoiceButton from '../../components/invoice/newInvoiceButton'
import { InvoiceFilterBadge } from '../../components/invoice/invoiceFilterBadge'
import { Link, useLocation } from 'react-router-dom'
import { ChevronDown, ChevronRight } from 'react-feather'
import InvoiceCount from '../../components/invoice/invoiceCount'
import { FormDate } from '../../components/ui/Form/Date'
import { DateTime } from 'luxon'
import { Client } from '../../../../back-end/common/client'
import { Staff } from '../../../../back-end/common/staff'
import { Director } from '../../constants/permission'
import { FormSelect, SelectOption } from '../../components/ui/Form/Select'
import { FormText } from '../../components/ui/Form/Text'
import { roundToTwo, dollarFormat } from '../../utilities/format'
import StatusPill from '../../components/invoice/statusPill'
import * as Paging from '../../components/ui/pagination/pagination'

import './invoices.css'
import InvoiceTotalCard from '../../components/invoice/invoiceTotalCard'
import { invoiceState } from '../../utilities/invoice'
import InvoiceAction, { canApprove, canSendToContact } from '../../components/invoice/invoiceAction'
import * as Roles from '../../constants/permission'
import { XeroOrg } from '../../../../back-end/common/xeroOrg'
import { AccountXeroOrg } from '../../../../back-end/common/accountXeroOrg'
import { ClientGroup } from '../../../../back-end/common/clientGroup'
import { useStateParams } from '../../utilities/hooks'

const canSeeBubbleRoles = [Roles.Admin, Roles.Director, Roles.SupervisorManager, Roles.CadetInterSenior]

const PAGE_SIZE = 1200

const Invoices = () => {
	const { appState, loading } = React.useContext(AppContext)
	const location = useLocation()

	const billySettings = appState.accounts.find((acc) => acc.accountID === appState.authState.attributes?.['custom:currentAccountId'])?.billySettings

	const [invoices, setInvoices] = React.useState<Invoice[] | null>(null)
	const [invoicePage, setInvoicePage] = React.useState<number>(1) // 0 means there's nothing more to get
	const [groups, setGroups] = React.useState<ClientGroup[] | null>(null)
	const [clients, setClients] = React.useState<Client[] | null>(null)
	const [staff, setStaff] = React.useState<Staff[] | null>(null)
	const [orgs, setOrgs] = React.useState<XeroOrg[] | null>(null)
	const [filterHasChangedWithoutSearch, setFilterHasChangedWithoutSearch] = React.useState<boolean>(false)
	const [paging, setPaging] = React.useState<Paging.FiltersState>({ page: 1, pageSize: Paging.getPagingPreferences() })
	const [filterError, setFilterError] = React.useState<string | null>(null)

	const [showSearchFilters, setShowSearchFilters] = React.useState<boolean>(false)
	const [statusFilter, setStatusFilter] = useStateParams<string[]>(
		[],
		'status',
		(d) => JSON.stringify(d),
		(d) => JSON.parse(d)
	)
	const [invoiceNoFilter, setInvoiceNoFilter] = useStateParams(
		'',
		'invoiceNo',
		(d) => d,
		(d) => d
	)
	const [groupFilter, setGroupFilter] = useStateParams(
		'',
		'group',
		(d) => d,
		(d) => d
	)
	const [clientFilter, setClientFilter] = useStateParams(
		'',
		'client',
		(d) => d,
		(d) => d
	)
	const [directorFilter, setDirectorFilter] = useStateParams(
		billySettings && !location.search.includes('?') ? billySettings.staffReportGroupXpmID || '' : '',
		'director',
		(d) => d,
		(d) => d
	)
	const [managerFilter, setManagerFilter] = useStateParams(
		'',
		'manager',
		(d) => d,
		(d) => d
	)
	const [invoicedFilter, setInvoicedFilter] = useStateParams(
		'',
		'invoicedBy',
		(d) => d,
		(d) => d
	)
	const [createdFilter, setCreatedFilter] = useStateParams(
		appState.permission.roleID === Roles.CadetInterSenior && appState.authState.attributes?.['custom:userId']
			? appState.authState.attributes?.['custom:userId']
			: '',
		'createdBy',
		(d) => d,
		(d) => d
	)
	const [startDateFilter, setStartDateFilter] = useStateParams<DateTime | null>(
		billySettings ? DateTime.fromISO(billySettings.agedWipDate).startOf('month') : null,
		'startDate',
		(d) => (d ? d.toISODate() || '' : ''),
		(d) => (d ? DateTime.fromISO(d) : null)
	)
	const [endDateFilter, setEndDateFilter] = useStateParams<DateTime | null>(
		null,
		'endDate',
		(d) => (d ? d.toISODate() || '' : ''),
		(d) => (d ? DateTime.fromISO(d) : null)
	)

	React.useEffect(() => {
		setFilterHasChangedWithoutSearch(true)
		if (appState.permission.roleID === Roles.CadetInterSenior && !groupFilter && !directorFilter && !managerFilter && !clientFilter) {
			setFilterError('Must have at least one of Group, Partner, Manager, or Client set')
		} else {
			setFilterError(null)
		}
	}, [invoiceNoFilter, groupFilter, clientFilter, directorFilter, managerFilter, invoicedFilter, createdFilter, startDateFilter, endDateFilter])

	const buildWhereString = React.useCallback(() => {
		const whereStringArray = []
		if (invoiceNoFilter) {
			whereStringArray.push(`where=InvoiceNumber=~${invoiceNoFilter}`)
		}
		if (groupFilter) {
			whereStringArray.push(`where=clicg.ClientGroupID==${groupFilter}`)
		}
		if (clientFilter) {
			whereStringArray.push(`where=ClientID==${clientFilter}`)
		}
		if (directorFilter) {
			whereStringArray.push(`where=ClientAccountManagerStaffXpmID==${directorFilter}`)
		}
		if (managerFilter) {
			whereStringArray.push(`where=ClientJobManagerStaffXpmID==${managerFilter}`)
		}
		if (invoicedFilter) {
			whereStringArray.push(`where=XeroOrgID==${invoicedFilter}`)
		}
		if (createdFilter) {
			whereStringArray.push(`where=CreateUserID==${createdFilter}`)
		}
		if (startDateFilter) {
			whereStringArray.push(`where=InvoiceDate>=${startDateFilter.toISODate()}`)
		}
		if (endDateFilter) {
			whereStringArray.push(`where=InvoiceDate<=${endDateFilter.toISODate()}`)
		}
		return whereStringArray.join('&')
	}, [invoiceNoFilter, groupFilter, clientFilter, directorFilter, managerFilter, invoicedFilter, createdFilter, startDateFilter, endDateFilter])

	React.useEffect(() => {
		const getData = async () => {
			const where = buildWhereString()
			const [invoiceRes, directorRes, organisationRes, clientGroupRes] = await Promise.all([
				!filterError ? Request.get(`invoice?minify&page=1&pageSize=${PAGE_SIZE}${where !== '' ? `&${where}` : ''}`) : null,
				Request.get(`staff`),
				Request.get(`organisation?where=AccountXeroOrgIsBilly==1&where=XeroOrgTenantType==ORGANISATION`),
				Request.get(`clientgroup`),
				Request.getPagedData(`client`, 4000, (data) => setClients((clients) => [...(clients || []), ...(data as { clients: Client[] }).clients])),
			])

			if (invoiceRes) {
				setInvoices(invoiceRes.data.invoices)
				setInvoicePage(invoiceRes.data.invoices.length === PAGE_SIZE ? 2 : 0)
			}
			setStaff(directorRes.data.staff)
			setOrgs(organisationRes.data.accountXeroOrgs.map((axo: AccountXeroOrg) => axo.xeroOrg))
			setGroups(clientGroupRes.data.clientGroups)
		}

		if (appState.authState.isLoggedIn && !loading) {
			getData()
		}
	}, [appState.authState, loading])

	const handleGetMore = React.useCallback(
		async (startFresh?: boolean, hotRefresh?: boolean) => {
			if (invoicePage > 0 || startFresh || hotRefresh) {
				const where = buildWhereString()
				const invoiceRes = await Request.get(
					`invoice?minify&page=${startFresh ? 1 : invoicePage}&pageSize=${PAGE_SIZE}${where !== '' ? `&${where}` : ''}`
				)

				startFresh && setPaging({ ...paging, page: 1 }) // Set table page to 1 if a fresh start
				setInvoices((invoices) => (startFresh || hotRefresh ? invoiceRes.data.invoices : [...(invoices || []), ...invoiceRes.data.invoices]))
				setInvoicePage(startFresh ? 2 : invoiceRes.data.invoices.length === PAGE_SIZE ? invoicePage + 1 : 0)
				setFilterHasChangedWithoutSearch(false)
			}
		},
		[invoicePage, setInvoicePage, buildWhereString]
	)

	const handleSubmitApproval = React.useCallback(
		(invoice: Invoice) => {
			try {
				setInvoices((invoices) => invoices?.map((i) => (i.invoiceID === invoice.invoiceID ? { ...i, status: 'SUBMITTED' } : i)) || null)
				Request.put(`invoice/${invoice.invoiceID}`, { status: 'SUBMITTED' })
			} catch (err) {
				console.error(err)
				setInvoices((invoices) => invoices?.map((i) => (i.invoiceID === invoice.invoiceID ? invoice : i)) || null)
			}
		},
		[setInvoices]
	)

	const handleApprove = React.useCallback(
		(invoice: Invoice) => {
			try {
				setInvoices((invoices) => invoices?.map((i) => (i.invoiceID === invoice.invoiceID ? { ...i, status: 'AUTHORISED' } : i)) || null)
				Request.put(`invoice/${invoice.invoiceID}`, { status: 'AUTHORISED' })
			} catch (err) {
				console.error(err)
				setInvoices((invoices) => invoices?.map((i) => (i.invoiceID === invoice.invoiceID ? invoice : i)) || null)
			}
		},
		[setInvoices]
	)

	const handleBulkApprove = React.useCallback(
		async (invoiceIds: string[]) => {
			try {
				setInvoices((invoices) => invoices?.map((i) => (invoiceIds.includes(i.invoiceID) ? { ...i, status: 'AUTHORISED' } : i)) || null)
				Request.put(`invoice?where=InvoiceID=in(${invoiceIds.join(',')})`, { status: 'AUTHORISED' }) // even though I set to authorised the xero response will update it to paid for us
			} catch (err) {
				console.error(err)
			}
		},
		[setInvoices]
	)

	const handleSendToClient = React.useCallback(
		(invoice: Invoice, emailToClient: boolean) => {
			try {
				setInvoices((invoices) => invoices?.map((i) => (i.invoiceID === invoice.invoiceID ? { ...i, sentToContact: true } : i)) || null)
				Request.put(`invoice/${invoice.invoiceID}`, { sentToContact: true, emailToClient })
			} catch (err) {
				console.error(err)
				setInvoices((invoices) => invoices?.map((i) => (i.invoiceID === invoice.invoiceID ? invoice : i)) || null)
			}
		},
		[setInvoices]
	)

	const handleBulkSendToClient = React.useCallback(
		(emailToClient: boolean) => async (invoiceIds: string[]) => {
			try {
				setInvoices((invoices) => invoices?.map((i) => (invoiceIds.includes(i.invoiceID) ? { ...i, sentToContact: true } : i)) || null)
				Request.put(`invoice?where=InvoiceID=in(${invoiceIds.join(',')})`, { sentToContact: true, emailToClient })
			} catch (err) {
				console.error(err)
			}
		},
		[setInvoices]
	)

	const handleDelete = React.useCallback(
		(invoice: Invoice) => {
			try {
				setInvoices((invoices) => invoices?.filter((i) => i.invoiceID !== invoice.invoiceID) || null)
				Request.put(`invoice/${invoice.invoiceID}`, { status: 'DELETED' })
			} catch (err) {
				console.error(err)
				setInvoices((invoices) => invoices?.concat([invoice]) || null)
			}
		},
		[setInvoices]
	)

	const handleVoid = React.useCallback(
		(invoice: Invoice) => {
			try {
				setInvoices((invoices) => invoices?.map((i) => (i.invoiceID === invoice.invoiceID ? { ...i, status: 'VOIDED' } : i)) || null)
				Request.put(`invoice/${invoice.invoiceID}`, { status: 'VOIDED' })
			} catch (err) {
				console.error(err)
				setInvoices((invoices) => invoices?.map((i) => (i.invoiceID === invoice.invoiceID ? invoice : i)) || null)
			}
		},
		[setInvoices]
	)

	const handleFilterClick = (filter: string) =>
		filter === 'all'
			? setStatusFilter([])
			: setStatusFilter(statusFilter.includes(filter) ? statusFilter.filter((s) => s !== filter) : statusFilter.concat([filter]))

	const filteredInvoices = React.useMemo(
		() => invoices?.filter((i) => statusFilter.length === 0 || statusFilter.includes(invoiceState(i.status, i.sentToContact))) || [],
		[invoices, statusFilter]
	)

	const numberColumnCompare = (a: string, b: string) => Number(a) - Number(b)

	const groupOptions = React.useMemo(() => groups?.map((g) => ({ value: g.clientGroupID, label: g.name })), [groups])
	const clientOptions = React.useMemo(() => clients?.map((c) => ({ value: c.clientID, label: c.name })), [clients])
	const directorOptions = React.useMemo(() => staff?.filter((d) => d.permissionID === Director).map((d) => ({ value: d.xpmID, label: d.name })), [staff])
	const managerOptions = React.useMemo(() => staff?.map((d) => ({ value: d.xpmID, label: d.name })), [staff])
	const orgOptions = React.useMemo(() => orgs?.map((o) => ({ value: o.xeroOrgID!, label: o.name! })), [orgs])
	const createdOptions = React.useMemo(() => staff?.filter((s) => s.userID)?.map((s) => ({ value: s.userID!, label: s.name })), [staff])
	const totalWriteOffUp = React.useMemo(
		() => roundToTwo(filteredInvoices.reduce((total, invoice) => (total += invoice.writtenOff - invoice.writtenUp), 0)),
		[filteredInvoices]
	)
	const totalInvoice = React.useMemo(() => roundToTwo(filteredInvoices.reduce((total, invoice) => (total += invoice.subTotal), 0)), [filteredInvoices])

	return (
		<Container fluid>
			<Row className="background-light align-items-center py-3">
				<Col>
					<Row>
						<Col>
							<InvoiceCount
								totalInvoices={filteredInvoices.length}
								group={groupOptions?.find((g) => g.value === groupFilter)?.label}
								client={clientOptions?.find((c) => c.value === clientFilter)?.label}
								director={directorOptions?.find((d) => d.value === directorFilter)?.label}
								manager={managerOptions?.find((m) => m.value === managerFilter)?.label}
								invoiced={orgOptions?.find((m) => m.value === invoicedFilter)?.label}
								created={createdOptions?.find((m) => m.value === createdFilter)?.label}
								dateStart={startDateFilter || undefined}
								dateEnd={endDateFilter || undefined}
							/>
						</Col>
						<Col sm="auto">
							{!showSearchFilters && <div style={{ display: 'inline', paddingRight: '15px', color: 'red' }}>{filterError}</div>}
							<Button onClick={() => setShowSearchFilters(!showSearchFilters)} variant="secondary" className="small-filter-button">
								Filters {showSearchFilters ? <ChevronDown /> : <ChevronRight />}
							</Button>
						</Col>
					</Row>
					{showSearchFilters && (
						<>
							<Row className="py-3">
								<Col sm={12} md={3}>
									<FormLabel>Invoice No.</FormLabel>
									<FormText name="search" onChange={(e) => setInvoiceNoFilter(e.target.value)} value={invoiceNoFilter} />
								</Col>
								<Col sm={12} md={3}>
									<FormLabel>Group</FormLabel>
									<FormSelect
										name="group"
										onChange={(e) => setGroupFilter(e ? (e as SelectOption<string>).value : '')}
										options={groupOptions || []}
										value={groupFilter}
										isClearable
										isSearchable
									/>
								</Col>
								<Col sm={12} md={3}>
									<FormLabel>Client</FormLabel>
									<FormSelect
										name="company"
										onChange={(e) => setClientFilter(e ? (e as SelectOption<string>).value : '')}
										options={clientOptions || []}
										value={clientFilter}
										isClearable
										isSearchable
									/>
								</Col>
							</Row>
							<Row>
								<Col sm={12} md={3}>
									<FormLabel>Partner</FormLabel>
									<FormSelect
										name="partner"
										onChange={(e) => setDirectorFilter(e ? (e as SelectOption<string>).value : '')}
										options={directorOptions || []}
										value={directorFilter}
										isClearable
										isSearchable
									/>
								</Col>
								<Col sm={12} md={3}>
									<FormLabel>Manager</FormLabel>
									<FormSelect
										name="manager"
										onChange={(e) => setManagerFilter(e ? (e as SelectOption<string>).value : '')}
										options={managerOptions || []}
										value={managerFilter}
										isClearable
										isSearchable
									/>
								</Col>
								<Col sm={12} md={3}>
									<FormLabel>Invoiced By</FormLabel>
									<FormSelect
										name="invoiced"
										onChange={(e) => setInvoicedFilter(e ? (e as SelectOption<string>).value : '')}
										options={orgOptions || []}
										value={invoicedFilter}
										isClearable
										isSearchable
									/>
								</Col>
								<Col sm={12} md={3}>
									<FormLabel>Created By</FormLabel>
									<FormSelect
										name="created"
										onChange={(e) => setCreatedFilter(e ? (e as SelectOption<string>).value : '')}
										options={createdOptions || []}
										value={createdFilter}
										isClearable
										isSearchable
									/>
								</Col>
							</Row>
							<Row className="py-3">
								<Col sm={5} md={2}>
									<FormDate
										name="startDate"
										label="Start Date"
										value={startDateFilter?.toISODate()}
										onChange={(e) => setStartDateFilter(e.target.value === '' ? null : DateTime.fromISO(e.target.value))}
									/>
								</Col>
								<Col sm={5} md={2}>
									<FormDate
										name="endDate"
										label="End Date"
										value={endDateFilter?.toISODate()}
										onChange={(e) => setEndDateFilter(e.target.value === '' ? null : DateTime.fromISO(e.target.value))}
									/>
								</Col>
								<Col style={{ display: 'flex', alignItems: 'flex-end' }}>
									<div
										style={{
											width: '100%',
											height: '50px',
											alignItems: 'center',
											flexDirection: 'row-reverse',
											display: 'flex',
											color: 'red',
										}}
									>
										{filterError}
									</div>
								</Col>
								<Col sm={2} md={2} style={{ alignSelf: 'flex-end' }}>
									<Button
										onClick={() => handleGetMore(true)}
										style={{ width: '100%' }}
										disabled={!filterHasChangedWithoutSearch || !!filterError}
									>
										Search
									</Button>
								</Col>
							</Row>
						</>
					)}
				</Col>
			</Row>
			{canSeeBubbleRoles.includes(appState.permission.roleID) && (
				<Row>
					<Col sm={4}>{invoices && <InvoiceTotalCard heading="Total Invoiced" value={totalInvoice} colour={'color-royal-blue'} />}</Col>
					<Col sm={4}>{invoices && <InvoiceTotalCard heading="Total Written Off/Up" value={totalWriteOffUp} colour={'color-green'} />}</Col>
				</Row>
			)}
			<Row>
				<Col>
					<Listing<Invoice>
						list={filteredInvoices}
						selectedActions={([] as ListingAction[])
							.concat(canApprove.includes(appState.permission.roleID) ? [{ name: 'Bulk Approve', method: handleBulkApprove }] : [])
							.concat(
								canSendToContact.includes(appState.permission.roleID)
									? [
											{ name: 'Bulk Send to Clients', method: handleBulkSendToClient(true) },
											{ name: 'Bulk Mark as Sent', method: handleBulkSendToClient(false) },
									  ]
									: []
							)}
						columns={[
							{
								value: (item) => item.number || '',
								render: (item) => (
									<div className="listing-group">
										<Link
											to={`/invoice/${item.invoiceID}${
												item.status === 'DRAFT' ? `/${item.lineItems && item.lineItems.length > 0 ? 'narration' : 'allocation'}` : ''
											}`}
											state={{
												queryString: location.search,
											}}
										>
											{item.number || 'No Invoice Number Yet'}
										</Link>
									</div>
								),
								showHeader: true,
								headerText: 'Invoice No',
								sortColumnName: 'invoiceNo',
							},
							{
								value: (item) => item.client?.clientGroup?.name || '',
								render: (item) => <div>{item.client?.clientGroup?.name}</div>,
								showHeader: true,
								headerText: 'Group',
								sortColumnName: 'clientClientGroup',
							},
							{
								value: (item) => item.client?.name || '',
								render: (item) => <div>{item.client?.name}</div>,
								showHeader: true,
								headerText: 'Client Name',
								sortColumnName: 'clientName',
							},
							{
								value: (item) => String(item.minimumTotal),
								render: (item) => <div className="text-end">{dollarFormat(item.minimumTotal, true, true)}</div>,
								customSortCompare: numberColumnCompare,
								showHeader: true,
								headerText: 'Invoice Total',
								headerClass: 'text-end',
								sortColumnName: 'invoiceTotal',
							},
							{
								value: (item) => String(item.writtenOff - item.writtenUp),
								render: (item) => <div className="text-end">{dollarFormat(item.writtenOff - item.writtenUp, true, true)}</div>,
								customSortCompare: numberColumnCompare,
								showHeader: true,
								headerText: 'Written Off/Up',
								headerClass: 'text-end',
								sortColumnName: 'writtenOff',
							},
							{
								value: (item) =>
									String(((item.writtenOff - item.writtenUp) / (item.minimumTotal + item.writtenOff - item.writtenUp || 1)) * 100),
								render: (item) => {
									const value = roundToTwo(
										((item.writtenOff - item.writtenUp) / (item.minimumTotal + item.writtenOff - item.writtenUp || 1)) * 100
									)
									return <div className={'text-end' + (value > 30 ? ' text-danger' : '')}>{value < 0 ? `(${-value}%)` : `${value}%`}</div>
								},
								customSortCompare: numberColumnCompare,
								showHeader: true,
								headerText: 'Write Off/Up %',
								headerClass: 'text-end',
								sortColumnName: 'writtenOffPercent',
							},
							{
								value: (item) => String(item.amountPaid),
								render: (item) => <div className="text-end">{dollarFormat(item.amountPaid, true, true)}</div>,
								customSortCompare: numberColumnCompare,
								showHeader: true,
								headerText: 'Paid',
								headerClass: 'text-end',
								sortColumnName: 'paid',
							},
							{
								value: (item) => String(item.amountDue),
								render: (item) => <div className="text-end">{dollarFormat(item.amountDue, true, true)}</div>,
								customSortCompare: numberColumnCompare,
								showHeader: true,
								headerText: 'Due',
								headerClass: 'text-end',
								sortColumnName: 'due',
							},
							{
								value: (item) => `${item.createUser.firstName} ${item.createUser.lastName}`,
								render: (item) => <div>{`${item.createUser.firstName} ${item.createUser.lastName}`}</div>,
								showHeader: true,
								headerText: 'Created By',
								sortColumnName: 'createdBy',
							},
							{
								value: (item) => item.date || '',
								render: (item) => <div>{DateTime.fromISO(item.date).toFormat('dd/MM/yyyy')}</div>,
								showHeader: true,
								headerText: 'Date',
								sortColumnName: 'date',
							},
							{
								value: (item) => item.xeroOrg.name || '',
								render: (item) => <div>{item.xeroOrg.name}</div>,
								showHeader: true,
								headerText: 'Invoiced By',
								sortColumnName: 'invoicedBy',
							},
							{
								value: (item) => invoiceState(item.status, item.sentToContact),
								render: (item) => <StatusPill status={invoiceState(item.status, item.sentToContact)} />,
								showHeader: true,
								headerText: 'Status',
								sortColumnName: 'status',
							},
							{
								value: () => '',
								render: (item) => (
									<InvoiceAction
										invoice={item}
										handleApprove={handleApprove}
										handleDelete={handleDelete}
										handleSendToClient={handleSendToClient}
										handleSubmitApproval={handleSubmitApproval}
										handleVoid={handleVoid}
									/>
								),
								showHeader: true,
								headerText: 'Actions',
							},
						]}
						customButtons={[<NewInvoiceButton key="add" />]}
						customFilters={[
							<InvoiceFilterBadge
								key="all"
								text="All"
								amount={invoices?.length || 0}
								handleClick={handleFilterClick}
								selected={statusFilter.length === 0}
							/>,
							<InvoiceFilterBadge
								key="draft"
								text="Draft"
								amount={invoices?.filter((i) => invoiceState(i.status, i.sentToContact) === 'draft').length || 0}
								handleClick={handleFilterClick}
								selected={statusFilter.includes('draft')}
							/>,
							<InvoiceFilterBadge
								key="awaiting-approval"
								text="Awaiting Approval"
								amount={invoices?.filter((i) => invoiceState(i.status, i.sentToContact) === 'awaiting-approval').length || 0}
								handleClick={handleFilterClick}
								selected={statusFilter.includes('awaiting-approval')}
							/>,
							<InvoiceFilterBadge
								key="approved"
								text="Approved"
								amount={invoices?.filter((i) => invoiceState(i.status, i.sentToContact) === 'approved').length || 0}
								handleClick={handleFilterClick}
								selected={statusFilter.includes('approved')}
							/>,
							<InvoiceFilterBadge
								key="awaiting-payment"
								text="Awaiting Payment"
								amount={invoices?.filter((i) => invoiceState(i.status, i.sentToContact) === 'awaiting-payment' && i.sentToContact).length || 0}
								handleClick={handleFilterClick}
								selected={statusFilter.includes('awaiting-payment')}
							/>,
							<InvoiceFilterBadge
								key="paid"
								text="Paid"
								amount={invoices?.filter((i) => invoiceState(i.status, i.sentToContact) === 'paid').length || 0}
								handleClick={handleFilterClick}
								selected={statusFilter.includes('paid')}
							/>,
						]}
						name="Invoice"
						namePlural="Invoices"
						getIDFunc={(item) => item.invoiceID}
						defaultSort={{ column: 'id', order: 'ASC' }}
						isLoading={invoices === null}
						paginationControls="both"
						sectionStyles={listingStyle}
						borderRadius
						boxShadow
						smallPagination
						headerPadRow
						hideDisplayCount
						pageFilter={paging}
						setPageFilter={setPaging}
						handleGetMore={invoicePage > 0 ? handleGetMore : undefined}
						handleHotRefresh={() => handleGetMore(false, true)}
					/>
				</Col>
			</Row>
		</Container>
	)
}

const listingStyle: SectionStyles = {
	trDepth0: { background: '#FFF' },
	trDepth1: { background: '#FFF' },
	trDepth2: { background: '#FFF' },
}

export default Invoices
